<script setup>
import { computed } from 'vue'

const emit = defineEmits(['show-whats-new'])

import { usePairingStore } from '@/stores/PairingStore.js'
import { useParticipateStore } from '@/stores/ParticipateStore.js'
import { useSettingsStore } from '@/stores/SettingsStore.js'
import { useTableStore } from '@/stores/TableStore.js'

const pairingStore = usePairingStore()
const participateStore = useParticipateStore()
const settingsStore = useSettingsStore()
const tableStore = useTableStore()

function resetDataAndSettings() {
    pairingStore.reset()
    participateStore.reset()
    settingsStore.reset()
    tableStore.reset()
}

function resetData() {
    pairingStore.reset()
    participateStore.reset()
    tableStore.reset()
}

const columnOptionVisual = computed(() => {
    if (settingsStore.numberOfColumns == 0) {
        return 'Auto'
    }

    return settingsStore.numberOfColumns
})

const fontSizeOptions = [
    {value: 6, text: 'Regular'},
    {value: 5, text: 'Large'},
    {value: 4, text: 'Extra Large'}
]
</script>

<template>
    <div class="card mb-4">
        <div class="card-header">
            <strong>Settings</strong>
        </div>
        <div class="card-body">
            <div class="vstack gap-3 mx-auto">
                <div>
                    <label for="fontSizeSettingsSelection" class="form-label">Font Size</label>
                    <select class="form-select" id="fontSizeSettingsSelection" v-model="settingsStore.fontSize">
                        <option v-for="option in fontSizeOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                    </select>
                </div>
                <div>
                    <label for="columnPicker" class="form-label">Columns: {{ columnOptionVisual }}</label>
                    <input id="columnPicker" class="form-range" v-model="settingsStore.numberOfColumns" type="range" min="0" max="6">
                </div>
                <div>
                    <label for="tableSizePicker" class="form-label">Table Size: {{ settingsStore.tableSize }}</label>
                    <input id="tableSizePicker" class="form-range" v-model="settingsStore.tableSize" type="range" min="2" max="10">
                </div>
                <div>
                    <label for="participateLimit" class="form-label">Participate Limit</label>
                    <input id="participateLimit" class="form-control" v-model="settingsStore.participateLimit" type="number" min="1" placeholder="Example: 40">
                </div>
                <div>
                    <label for="tableCountStart" class="form-label">Table Count Start</label>
                    <input id="tableCountStart" class="form-control" v-model="settingsStore.tableCountStart" type="number" min="1" placeholder="Example: 10">
                </div>
                <div>
                    <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" role="switch" id="theme-dark-mode-toggle" v-model="settingsStore.darkModeToggle">
                        <label class="form-check-label" for="theme-dark-mode-toggle">Dark Mode</label>
                    </div>
                </div>
                <hr />
                <button class="btn btn-warning btn-sm" type="button" id="howToUseModalButton" data-bs-toggle="modal" data-bs-target="#howToUseModal">How to Use</button>
                <button class="btn btn-info btn-sm" type="button" @click="emit('show-whats-new')">What's New</button>
                <button class="btn btn-danger btn-sm" type="button" id="resetAllAppDataButton" data-bs-toggle="modal" data-bs-target="#resetAllModal">Reset App</button>
            </div>
        </div>

        <!-- Reset All Modal -->
        <div class="modal fade" id="resetAllModal" tabindex="-1" aria-labelledby="resetAllModalLabel" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h1 class="modal-title fs-5" id="resetAllModalLabel">Resetting App</h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <ul>
                            <li><strong>Reset All Data:</strong> Resets all data including participates, pairings, and tables.</li>
                            <li><strong>Reset All Data &amp; Settings:</strong> Resets all data including participates, pairings, tables, and settings.</li>
                        </ul>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-danger" data-bs-dismiss="modal" @click="resetData">Reset All Data</button>
                        <button type="button" class="btn btn-danger" data-bs-dismiss="modal" @click="resetDataAndSettings">Reset All Data &amp; Settings</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- How to Use Modal -->
        <div class="modal fade" id="howToUseModal" tabindex="-1" aria-labelledby="howToUseModalLabel" aria-hidden="true">
            <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h1 class="modal-title fs-5" id="howToUseModalLabel">How to Use</h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <ul class="nav nav-tabs mb-3" role="tablist">
                            <li class="nav-item" role="presentation">
                                <button class="nav-link active" data-bs-toggle="tab" data-bs-target="#htu-start" type="button" role="tab">Getting Started</button>
                            </li>
                            <li class="nav-item" role="presentation">
                                <button class="nav-link" data-bs-toggle="tab" data-bs-target="#htu-participants" type="button" role="tab">Participants</button>
                            </li>
                            <li class="nav-item" role="presentation">
                                <button class="nav-link" data-bs-toggle="tab" data-bs-target="#htu-tables" type="button" role="tab">Tables</button>
                            </li>
                            <li class="nav-item" role="presentation">
                                <button class="nav-link" data-bs-toggle="tab" data-bs-target="#htu-pairings" type="button" role="tab">Pairings</button>
                            </li>
                            <li class="nav-item" role="presentation">
                                <button class="nav-link" data-bs-toggle="tab" data-bs-target="#htu-settings" type="button" role="tab">Settings & Shortcuts</button>
                            </li>
                        </ul>

                        <div class="tab-content">

                            <!-- Getting Started -->
                            <div class="tab-pane fade show active" id="htu-start" role="tabpanel">
                                <p>Cardinate randomly assigns participants to numbered tables for tabletop gaming events. Everything is saved automatically to your browser — just open the app and pick up where you left off.</p>
                                <h6 class="fw-bold">Basic workflow</h6>
                                <ol>
                                    <li>Add participants using the name field in the sidebar (or use <strong>Bulk Entry</strong> for large groups).</li>
                                    <li>Optionally set up <strong>Preferred Pairings</strong> for groups that must sit together.</li>
                                    <li>Click <strong>Randomly Assign All</strong> — or press <kbd>R</kbd> — to seat everyone.</li>
                                    <li>Drag participants between tables to make any manual adjustments.</li>
                                </ol>
                                <p class="text-body-secondary mb-0"><small>All data is stored in your browser's local storage. Nothing is sent to a server. Clearing browser data will wipe the app.</small></p>
                            </div>

                            <!-- Participants -->
                            <div class="tab-pane fade" id="htu-participants" role="tabpanel">
                                <table class="table">
                                    <thead>
                                        <tr>
                                            <th scope="col" style="width: 30%">Feature</th>
                                            <th scope="col">Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <th scope="row">Add participant</th>
                                            <td>Type a name and press Enter or click Add.</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Bulk Entry</th>
                                            <td>Click the dropdown arrow next to the name field. Paste or type names one per line. Supports comma- or tab-separated first/last name pairs, with a choice of First→Last or Last→First order. A preview shows exactly what will be added.</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">View All</th>
                                            <td>Opens a searchable list of all participants. You can edit names or delete participants from here.</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Locking</th>
                                            <td>Locked participants cannot be dragged and are kept in place when you run Randomly Assign All. Participants are automatically locked after each assignment. Use <strong>Unlock All</strong> before re-assigning if you want everyone reshuffled.</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Unassigned badges</th>
                                            <td>Any participant not yet seated appears as a draggable badge below the action buttons. Drag them directly onto a table to seat them manually.</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Participation Limit</th>
                                            <td>Sets the maximum number of participants. A progress bar shows current usage. Configurable in Settings.</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <!-- Tables -->
                            <div class="tab-pane fade" id="htu-tables" role="tabpanel">
                                <table class="table">
                                    <thead>
                                        <tr>
                                            <th scope="col" style="width: 30%">Feature</th>
                                            <th scope="col">Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <th scope="row">Fill badge</th>
                                            <td>Each table shows a <strong>seated / capacity</strong> badge (e.g. 2/6). It turns green when the table is full.</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Rename a table</th>
                                            <td>Double-click any table header to rename it. Press Enter to save or Escape to cancel.</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Drag &amp; drop</th>
                                            <td>Drag any unlocked participant from one table to another. Locked participants cannot be moved.</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Lock / Unlock</th>
                                            <td>Use the lock icon next to a participant to toggle them individually, the lock button on a table header to lock everyone at that table, or <strong>Lock All / Unlock All</strong> to affect every participant at once.</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Add / Remove tables</th>
                                            <td>Tables are created automatically as you add participants, but you can add extras manually. Empty tables can be deleted with the ✕ button (visible when the sidebar is open). <strong>Remove Unused Tables</strong> clears all empty tables at once.</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Table Count Start</th>
                                            <td>Controls what number the first table is labeled. Set to 10, for example, to start at "Table 10". Configurable in Settings.</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <!-- Pairings -->
                            <div class="tab-pane fade" id="htu-pairings" role="tabpanel">
                                <p>Preferred Pairings let you pre-assign groups that should always sit together — for example, friends or pre-registered teams. Randomly Assign All will always keep a pairing group at the same table.</p>
                                <table class="table">
                                    <thead>
                                        <tr>
                                            <th scope="col" style="width: 30%">Feature</th>
                                            <th scope="col">Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <th scope="row">Create a pairing</th>
                                            <td>Select participants from the dropdown, choose a pairing slot, and click Submit. Participants can only belong to one pairing at a time.</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Edit a pairing</th>
                                            <td>Use the Actions dropdown on any pairing to edit or delete it. Deleting a pairing removes the pairing assignment from all participants in it.</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Error status</th>
                                            <td>A pairing marked <strong>Error</strong> means the group is larger than your configured Table Size. Either reduce the pairing size or increase the Table Size in Settings.</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <!-- Settings & Shortcuts -->
                            <div class="tab-pane fade" id="htu-settings" role="tabpanel">
                                <h6 class="fw-bold">Settings</h6>
                                <table class="table mb-4">
                                    <thead>
                                        <tr>
                                            <th scope="col" style="width: 30%">Setting</th>
                                            <th scope="col">Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <th scope="row">Table Size</th>
                                            <td>Number of seats per table (2–10). Default: 4. Affects auto table creation and pairing validation.</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Columns</th>
                                            <td>Number of columns in the table grid. 0 = auto-responsive layout.</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Font Size</th>
                                            <td>Applies globally: Regular, Large, or Extra Large.</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Participation Limit</th>
                                            <td>Maximum number of participants. Default: 60.</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Table Count Start</th>
                                            <td>The number the first table is labeled. Default: 1.</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Dark Mode</th>
                                            <td>Toggles the light/dark theme.</td>
                                        </tr>
                                    </tbody>
                                </table>

                                <h6 class="fw-bold">Keyboard Shortcuts</h6>
                                <table class="table mb-0">
                                    <thead>
                                        <tr>
                                            <th scope="col" style="width: 30%">Key</th>
                                            <th scope="col">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <th scope="row"><kbd>H</kbd></th>
                                            <td>Toggle the sidebar</td>
                                        </tr>
                                        <tr>
                                            <th scope="row"><kbd>R</kbd></th>
                                            <td>Randomly assign all participants</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <p class="text-body-secondary mt-2 mb-0"><small>Shortcuts won't fire when a text field or modal is focused.</small></p>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
</style>
